import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  RocketOutlined,
  PictureOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  BellOutlined,
  SafetyOutlined,
  BarChartOutlined,
  ShopOutlined,
  CodeOutlined,
  FileTextOutlined,
  SnippetsOutlined,
  BookOutlined,
  HomeOutlined,
  SearchOutlined,
  AppstoreOutlined,
  ApiOutlined,
  IdcardOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Layout as AntLayout, Menu, Button, theme, Dropdown, Badge, Avatar, Modal, Form, Input, message } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const { Header, Sider, Content } = AntLayout;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表盘',
    },
    {
      key: '/business-managers',
      icon: <ShopOutlined />,
      label: 'BM 管理',
    },
    {
      key: '/public-pages',
      icon: <HomeOutlined />,
      label: '公共主页',
    },
    {
      key: '/advertiser-identities',
      icon: <IdcardOutlined />,
      label: '主体管理',
    },
    {
      key: '/applications',
      icon: <AppstoreOutlined />,
      label: '应用管理',
    },
    {
      key: '/fb-usage',
      icon: <ApiOutlined />,
      label: 'API 用量',
    },
    {
      key: '/developer-accounts',
      icon: <CodeOutlined />,
      label: '开发者账号',
    },
    {
      key: '/fb-users',
      icon: <UserOutlined />,
      label: 'FB 用户管理',
    },
    {
      key: '/campaigns',
      icon: <RocketOutlined />,
      label: '广告系列',
    },
    {
      key: '/campaign-lookup',
      icon: <SearchOutlined />,
      label: '系列查询',
      disabled: true,
    },
    {
      key: '/campaign-templates',
      icon: <FileTextOutlined />,
      label: '系列模板(旧)',
    },
    {
      key: '/ad-series-templates',
      icon: <FileTextOutlined />,
      label: '广告系列模板',
    },
    {
      key: '/ad-creation',
      icon: <RocketOutlined />,
      label: '创建广告',
    },
    {
      key: '/ad-creation/drafts',
      icon: <SnippetsOutlined />,
      label: '草稿箱',
    },
    {
      key: '/creative-copy',
      icon: <BookOutlined />,
      label: '文案库',
    },
    {
      key: '/materials',
      icon: <PictureOutlined />,
      label: '素材管理',
    },
    {
      key: '/material-collections',
      icon: <FileTextOutlined />,
      label: '素材集管理',
    },
    {
      key: '/tasks',
      icon: <RocketOutlined />,
      label: '任务管理',
    },
    {
      key: '/auto-rules',
      icon: <SafetyOutlined />,
      label: '自动规则',
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: '数据报表',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  const disabledMenuKeys = new Set(['/campaign-lookup']);

  const handleMenuClick = ({ key }) => {
    if (disabledMenuKeys.has(key)) return;
    navigate(key);
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'change-password') {
      passwordForm.resetFields();
      setPasswordModalOpen(true);
    } else if (key === 'profile') {
      Modal.info({
        title: '个人资料',
        content: (
          <div>
            <p>用户名：{user?.username || '-'}</p>
            <p>邮箱：{user?.email || '-'}</p>
            <p>姓名：{user?.full_name || '-'}</p>
          </div>
        ),
      });
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setSavingPassword(true);
      await api.post('/auth/change-password', {
        old_password: values.old_password,
        new_password: values.new_password,
      });
      message.success('密码修改成功');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.detail || '修改密码失败');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="light">
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <h3 style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? 'FB' : 'FB Ads Pro'}
          </h3>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <AntLayout>
        <Header style={{ 
          padding: '0 24px', 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Badge count={5} size="small">
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username || '用户'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ 
          margin: '24px 16px', 
          padding: 24, 
          minHeight: 280,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          overflow: 'auto'
        }}>
          <Outlet />
        </Content>
      </AntLayout>
      <Modal
        title="修改密码"
        open={passwordModalOpen}
        onOk={handleChangePassword}
        onCancel={() => setPasswordModalOpen(false)}
        confirmLoading={savingPassword}
        destroyOnClose
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={passwordForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="old_password"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少 6 位' },
            ]}
          >
            <Input.Password placeholder="至少 6 位" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </AntLayout>
  );
};

export default Layout;
